/**
 * Flow step/run telemetry publication control: the projection every payload passes through before it can reach the
 * run and step SPAN payload previews (`flow.input.preview`, `flow.step.input.preview` / `.output.preview`) or the LOCAL
 * RUN JOURNAL's run and step rows (`composeRunRecordJournalLine`, `const/flow-run-journal-records.ts`) -- durable,
 * retained on-disk evidence -- and before the run-record envelope reaches the tail log. (No writer feeds the `wf_*`
 * tables; they are retained PCAOB-indefinite and every row already written is still there. Retention, not
 * readership, is what makes the projection load-bearing, and the live sinks above carry the same payloads, so nothing
 * relaxes it. The run-level `input` this projects is a tail-log payload only, which does not make projecting it
 * optional.)
 *
 * POSTURE: PUBLISH BY DEFAULT, REDACT WHAT IS SECRET (operator directive 2026-08-05). A value is withheld when its KEY
 * is secret-shaped or its CONTENT carries a credential span; everything else publishes verbatim. This INVERTS the
 * former deny-by-default permit list, and the reason is the one the previous header conceded without acting on:
 * "redacting everything is safe and useless". A corpus whose every field reads `<string:5>` cannot answer the question
 * it exists for -- whether the system is behaving as expected -- so it was read as broken and worked around, which is
 * what a control nobody trusts always produces.
 *
 * WHAT THIS COSTS, stated plainly rather than buried, because the inversion is real and the old header's MECHANISM was
 * not wrong. A task step's `input` is the PREVIOUS step's output, and for the first step-producing step it is the
 * ENTIRE flow input; `executeD1Crud` returns full row sets verbatim and doctrine confines it to task steps. So this
 * projection now publishes real business data, and the only things between a caller-posted credential and the published
 * corpus are the two SSOT-derived screens below. A secret in a key shape `SENSITIVE_KEY_PATTERNS` does not know,
 * carrying a value shape `SENSITIVE_VALUE_PATTERNS` / `CREDENTIAL_CONTEXT_PATTERNS` do not know, WILL publish. That
 * residual is accepted deliberately; the cure is extending those SSOTs, or keeping the credential out of the payload.
 * Never re-narrow here.
 *
 * WHAT AN EXTENSION ACTUALLY REACHES -- corrected 2026-08-07. This header previously said the SSOTs are shared by
 * "every other redaction surface in the tree, so an addition protects all of them", which was false in two directions
 * and is exactly the kind of belief that lets a silent leak sit unnoticed. Probed by grepping each import at HEAD:
 * a `SENSITIVE_VALUE_PATTERNS` member DOES reach every value-side surface -- the direct `.some(...)` consumers
 * (`utils/security/redact-string.ts`, `tools/observability/secure-logger.ts`), every `scrubCredentialSpans` caller
 * (which as of 2026-08-07 includes `utils/deps/registry.ts`, whose console fallback reaches the array through
 * `redactTelemetryPayload` rather than importing it), and this projection. A `CREDENTIAL_CONTEXT_PATTERNS` member
 * reaches ONLY the `scrubCredentialSpans` callers -- the
 * `.some((pattern) => pattern.test(value))` consumers read the value array alone, so a context arm is invisible to them
 * by construction. (`sanitizePayload` in `tools/observability/activity-emit.ts` consumed NEITHER array until the
 * same 2026-08-07 change gave it a `scrubCredentialSpans` axis alongside its `redactPII` one.) So name the surface an
 * extension is meant to protect and check it consumes the array you extended.
 *
 * THE TWO SCREENS, both DERIVED from `const/redaction-patterns.ts` so this leaf owns no vocabulary of its own:
 *  - KEY screen -- `SENSITIVE_KEY_PATTERNS` (api/access key, client secret, secret, token, bearer, password,
 *    credential, authorization) plus the literal `SENSITIVE_FIELDS` set, which additionally enumerates the regulated
 *    identifiers `ssn` / `credit_card` / `bank_account`. Those stay redacted under this posture: they are a compliance
 *    exposure rather than a debugging preference, and they were already members of the shared sensitive-field SSOT
 *    rather than a list invented here.
 *  - VALUE screen -- `scrubCredentialSpans`, the SPAN-preserving scrubber. A string keeps everything around a
 *    credential and loses only the credential, which is what lets a diagnostic value stay diagnostic. This is the
 *    treatment the error leg already used, now applied to the payload legs too.
 *
 * THE CAPS ARE A WIRE BUDGET, NOT A SECRECY CONTROL, and they were re-derived for this posture. Under the permit list
 * every payload compressed to descriptors, so rows never approached their ceiling and the old numbers (5 array items,
 * 24 keys, 64-char values, 4,000-char rows) cost nothing. Publishing real values makes each one a visible truncation,
 * and the row ceiling in particular would have made the inversion SELF-DEFEATING: `boundStepResultsForTransport` strips
 * `input`/`output` from any row over it, so the data-rich steps this change exists to reveal are exactly the ones that
 * would have gone blank.
 *
 * IDEMPOTENT BY CONSTRUCTION, which is what lets the control stack across its three layers (the row mapper, both
 * run-record transports, and the flow-sync pipeline writer). A descriptor string, the redaction placeholder and an
 * already-scrubbed span all re-project to themselves, and a capped string carries the marker that short-circuits a
 * second cut, so a payload through all three is byte-identical to one through one.
 *
 * HOMED IN const/ ON PURPOSE, same reason as `const/signed-token-codec.ts`: one consumer is `utils/flow-engine/
 * step-results-map.ts`, a pure utils leaf, and a utils-to-utils value import is a `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`
 * violation with no allowlist -- so const/ is the one shared home the layer floor permits for the tools-side transports
 * and that utils leaf together.
 *
 * Paired forbid-rule: `SEC-FLOW-TELEMETRY-SECRET-KEY-EMISSION-001`. Under this posture it governs flow `inputSchema`
 * declarations of secret-shaped properties and any surface hand-rolling a `node_results` emission outside the
 * sanctioned transports; its former `permit-list` arm retired with the permit list itself.
 */

import { numberOrZero } from '@federa/const/number-guards';
import { stringOrNull } from '@federa/const/string-guards';
import { matchesAnyPattern } from './regex-flagless-test.ts';
import { isPlainObject } from './record-guards.ts';
import { REDACTED_PLACEHOLDER } from './redaction.ts';
import { SENSITIVE_FIELDS, SENSITIVE_KEY_PATTERNS, scrubSensitiveSpans } from './redaction-patterns.ts';
import type { TelemetryTruncationReason } from '@federa/types/api-flow';

/** Descriptor kind vocabulary. A projected value is `<kind>` or `<kind:count>`; consumers match names, never literals. */
export const TELEMETRY_REDACTION_DESCRIPTORS = {
	STRING: 'string',
	NUMBER: 'number',
	BOOLEAN: 'boolean',
	BIGINT: 'bigint',
	FUNCTION: 'function',
	SYMBOL: 'symbol',
	UNKNOWN: 'unknown',
	DEPTH_CAPPED: 'depth-capped',
	DYNAMIC_KEYS: 'dynamic-keys',
	KEYS_TRUNCATED: 'keys-truncated',
	ITEMS_TRUNCATED: 'items-truncated',
	CHARS_TRUNCATED: 'chars-truncated',
} as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): WHY a published payload leg reads `null` -- the VALUE half of
 * `TelemetryTruncationReason` (`types/api-flow.ts`, which carries the measurement that obliged it and why an ABSENT
 * member is the only honest default).
 *
 * IT SITS BESIDE THE CAPS DELIBERATELY, in the same leaf that owns the numbers those cuts are made against. A marker
 * vocabulary homed away from its ceilings is one edit from naming a bound nobody applies any more; here a re-derivation
 * of `ROW_MAX_JSON_CHARS` and the word a reader is handed for it are the same diff.
 *
 * IT IS THE PAYLOAD-LEG SIBLING OF {@link TELEMETRY_REDACTION_DESCRIPTORS}, not a second spelling of it: a descriptor
 * marks a value that survived in TRUNCATED form (`<chars-truncated:N>` rides IN the string), while these three mark a
 * leg that survived in NO form -- there is no truncated payload to carry a marker, so the fact has to ride the row.
 *
 * `satisfies` rather than a bare `as const`, exactly as `FLOW_RUN_JOURNAL_RECORD_KINDS` does, so a reason added here
 * that the union does not declare fails at this line rather than becoming a token no reader recognises.
 */
export const TELEMETRY_TRUNCATION = {
	/** The row exceeded `ROW_MAX_JSON_CHARS` on its OWN account (or could not be serialized at all). */
	ROW_CAP: 'row-cap',
	/** The row was sacrificed heaviest-first so its SIBLINGS could ride the caller's aggregate budget. */
	TRANSPORT_BUDGET: 'transport-budget',
	/** A `run-intent` journal line's `input` exceeded the same ceiling (`composeRunIntentJournalLine`). */
	INTENT_INPUT_CAP: 'intent-input-cap',
	/** A settled journal line's `outputSnapshot` exceeded the same ceiling (`composeRunRecordJournalLine`). */
	RECORD_OUTPUT_SNAPSHOT_CAP: 'record-output-snapshot-cap',
} as const satisfies Record<string, TelemetryTruncationReason>;

/**
 * THE tunables, re-derived for the publish-by-default posture (see the header's wire-budget paragraph). Consts, never
 * env vars: the publication posture must be identical in every runtime and reviewable in the diff, and an env var would
 * let one deployment publish what another redacts with nothing in the tree saying so.
 *
 * Module-local unless a consumer actually imports one -- a tunable is tuned by editing it here, not by exporting it,
 * and an exported const with no reader is dead surface (`QUAL-DEAD-EXPORT-001`). The FOUR with real importers are
 * published together as `TELEMETRY_REDACTION` below: the row ceiling is read by the transport bound in
 * `utils/flow-engine/step-results-transport-bound.ts`, and the array, value and error caps are asserted by the projection's own
 * pins rather than restated as literals there.
 */
const TELEMETRY_REDACTION_MAX_DEPTH = 12;
const TELEMETRY_REDACTION_MAX_OBJECT_KEYS = 128;
/** Key NAMES are published only when identifier-shaped and within this cap; anything else (an email or token used as a map key) is bucketed under one `<dynamic-keys:N>` entry. */
const TELEMETRY_REDACTION_KEY_NAME_MAX_CHARS = 48;

/**
 * The FOUR tunables with importers, as one grouped export (the `HTTP_STATUS` shape) rather than four flat ones: they are
 * a single wire budget divided into legs, and the row / value / error ceilings are only reviewable against each other.
 * The unexported siblings above stay module-local -- an exported const with no reader is dead surface.
 */
export const TELEMETRY_REDACTION = {
	/** Array members published before the tail collapses to one `<items-truncated:N>` descriptor. */
	MAX_ARRAY_ITEMS: 100,
	/**
	 * A published STRING survives only within this cap; a longer one is cut with a visible `<chars-truncated:N>` marker.
	 *
	 * A WIRE bound, not a secrecy one -- the secrecy decision is the key screen plus `scrubCredentialSpans`, and length was
	 * never a proxy for either (a signed guest claim is ~44 characters and cleared the previous 64-char cap outright).
	 * Sized so an ordinary field, a serialized row, a prompt fragment or a stack-shaped string arrives whole.
	 */
	PUBLISHABLE_VALUE_MAX_CHARS: 4_000,
	/**
	 * Per-ROW wire ceiling for `boundStepResultsForTransport`.
	 *
	 * SIZED as an eighth of the 256,000-char aggregate node-results budget the run-record door POST once carried, so a
	 * run's several heaviest rows can each arrive at full weight before the heaviest-first stripper engages; that budget
	 * left with its door, and this number stands on its own now. The former 4,000 was sized when every payload compressed
	 * to descriptors; against real published values it stripped precisely the data-rich rows worth reading. Re-sizing it
	 * is a measurement against the live rows, never a re-derivation off another budget.
	 */
	ROW_MAX_JSON_CHARS: 32_000,
	/**
	 * Wire ceiling for the `error` leg, and a SEPARATE number from the row ceiling above on purpose -- reusing that one
	 * would borrow a name rather than a derivation, since it bounds a whole serialized ROW while this bounds ONE free-text
	 * field inside it.
	 *
	 * DERIVED FROM the largest single error message this tree can produce. That is the agent crash diagnostic
	 * (`buildEnrichedFailureMessage`, `utils/agents/result-proc-enriched-message.ts`): a short base --
	 * `<name> exited with code <code> [reason=<reason>]` -- plus a ` -- log tail: ` separator plus at most
	 * `MESSAGE_TAIL_LIMIT` characters of log excerpt (`const/config-investigation.ts`). This cap
	 * sits above that with headroom, so a COMPLETE single diagnostic is never cut; the relationship is pinned by a test
	 * rather than left as prose, so raising `MESSAGE_TAIL_LIMIT` past this reds instead of silently truncating.
	 *
	 * WHY the leg needs a bound at all: `stripRowPayloads` (`utils/flow-engine/step-results-transport-bound.ts`) nulls `input`/`output`
	 * but KEEPS `error` by design, so an uncapped error is the one field the transport bound can never shed. Any producer
	 * that joins one diagnostic per failed sub-unit into a single message is unbounded in that count.
	 */
	ERROR_MAX_CHARS: 1_000,
} as const;

/** Identifier-shaped key name: a schema field name, not caller data. */
const TELEMETRY_REDACTION_KEY_NAME_PATTERN = /^[A-Za-z_$][A-Za-z0-9_$.-]*$/;

/** Recognizes an ALREADY-projected descriptor string, which is what makes the projection idempotent across its three layers. */
const TELEMETRY_REDACTION_DESCRIPTOR_PATTERN = /^<[a-z][a-z-]*(?::\d+)?>$/;

/** @runtime-derived-ssot-helper (const-leaf-pinned): compose one descriptor token. */
export function telemetryRedactionDescriptor(kind: string, count?: number): string {
	return count === undefined ? `<${kind}>` : `<${kind}:${count}>`;
}

/**
 * True when a KEY name is secret-shaped, so its value is withheld whatever the value looks like.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): both halves come from `const/redaction-patterns.ts` -- the regex
 * family and the literal set -- so this leaf tracks that SSOT's growth with no maintenance and no second vocabulary to
 * keep in sync. The literal set is consulted on the RAW key BEFORE the lowercased one because it enumerates both
 * casings of a payload KEY (accessToken and access_token -- key names, not repo symbols, so they carry no cite
 * backticks), and lowercasing first would miss every camelCase member.
 */
function isSecretKey(key: string): boolean {
	if (SENSITIVE_FIELDS.has(key) || SENSITIVE_FIELDS.has(key.toLowerCase())) return true;
	// The collection walk is `matchesAnyPattern` (`const/regex-flagless-test.ts`), which resets `lastIndex` per member,
	// rather than the `.some(...)` this spelled beside a comment asserting the array carries no `g` flag. The assertion
	// was true and is still pinned by that array's suite -- but it made a REDACTION verdict depend on a property of a
	// list this file does not own, in the direction that fails OPEN: a `/g` member added there would have made this
	// predicate answer false on every call after the first and published the secret it exists to withhold.
	return matchesAnyPattern(key, SENSITIVE_KEY_PATTERNS);
}

/** True for a string this projection already emitted, so re-projecting it returns it unchanged. */
function isDescriptorString(value: unknown): boolean {
	return typeof value === 'string' && TELEMETRY_REDACTION_DESCRIPTOR_PATTERN.test(value);
}

/** Marker THIS module wrote, so a second pass over an already-capped string leaves it alone rather than re-cutting it. */
const TELEMETRY_REDACTION_TRUNCATED_SUFFIX = new RegExp(`<${TELEMETRY_REDACTION_DESCRIPTORS.CHARS_TRUNCATED}:\\d+>$`);

/**
 * Cap a string at `limit`, keeping the HEAD and marking the cut VISIBLY.
 *
 * Head-preserving because the actionable part of a value or a failure description is at the front. Dropping silently
 * would make a truncated string indistinguishable from a genuinely short one -- the same "a reader must not invent a
 * cause" failure the empty-step-list corollary states against -- so the count of dropped characters rides in the marker.
 *
 * ONE implementation for both legs: the payload cap and the error cap differ only in their limit, and spelling the
 * head-plus-marker walk twice is how the two drift.
 */
function capString(value: string, limit: number): string {
	if (value.length <= limit || TELEMETRY_REDACTION_TRUNCATED_SUFFIX.test(value)) return value;
	const dropped = value.length - limit;
	return value.slice(0, limit) + telemetryRedactionDescriptor(TELEMETRY_REDACTION_DESCRIPTORS.CHARS_TRUNCATED, dropped);
}

/** typeof -> descriptor kind for values JSON cannot carry; an unlisted typeof falls back to UNKNOWN. */
const UNSERIALIZABLE_DESCRIPTOR_BY_TYPEOF: Readonly<Record<string, string>> = {
	bigint: TELEMETRY_REDACTION_DESCRIPTORS.BIGINT,
	function: TELEMETRY_REDACTION_DESCRIPTORS.FUNCTION,
	symbol: TELEMETRY_REDACTION_DESCRIPTORS.SYMBOL,
};

/**
 * THE VALUE SCREEN THIS PROJECTION APPLIES, and the gap it does not close.
 *
 * The screen is `scrubSensitiveSpans` (`const/redaction-patterns.ts`), which composes the credential and email passes
 * in that order. Both the pass and the composition used to live HERE -- the email pass as a module-private function,
 * the composition respelled at each of this leaf's two string sites -- which is exactly why the ai-telemetry ingest
 * door published addresses verbatim: a sibling surface cannot adopt a screen homed inside one consumer, and what it
 * could reach was only the credential half. Both moved to the vocabulary SSOT (tracker A23) and their reasoning
 * travelled with them, so it is not restated here.
 *
 * RECORDED GAP: phone / SSN / card / ADDRESS VALUES sitting under a non-secret KEY still publish. Their key names are
 * partly covered (`SENSITIVE_FIELDS` carries `ssn`, `credit_card`, `bank_account`; `address` is covered by NEITHER
 * array, and `flows/mm-aggregate.ts` requires one on its input) but their value SHAPES live in `PII_PATTERNS` inside
 * `const/pii-redaction.ts`.
 *
 * THE RECORDED CURE IS TWO OPERATIONS AND ONLY THE FIRST IS CONTAINED -- measured 2026-08-15, stated here so the next
 * reader does not attempt the second on the strength of the first. THE MOVE IS DONE (2026-08-24): the whole leaf
 * relocated from `utils/security/` to `const/pii-redaction.ts`, so the one-way layer floor no longer stands between
 * this projection and those shapes and the sentence above that cited it as the blocker has been struck. THE GAP IS
 * NOT CLOSED BY THAT, and the distinction is the whole point of this paragraph: the ADOPTION is the half that matters
 * and it remains untaken, so phone / SSN / card / address values under a non-secret key still publish exactly as
 * recorded. What changed is that the remaining obstacle is now solely the false-positive measurement below rather than
 * the layering. The reason adoption is not free: consuming those shapes from this projection injects them into the span
 * payload previews, the run journal's run and step rows, the tail log and now the kaizen `output_snapshot`, and their false-positive rate against the
 * text this projection is actually handed is worse than their hit rate. Measured over a 14-string corpus of realistic
 * error and output text: 3 true positives, 5 FALSE ones. `address` is the loud arm -- its `[A-Za-z0-9\s,]+` body
 * crosses spaces and commas while `St` / `Ct` / `Pl` / `Way` / `Dr` sit as bare alternatives, so it swallowed whole
 * clauses out of ordinary prose (`"12 files changed, 3 insertions Way"`, `"1042 rows in 2 Pl"`); `creditCard` ate a
 * 16-digit grouped ORDER id. Publishing the address placeholder (`REDACTION_PLACEHOLDERS`, `const/redaction.ts`) in place of a scan summary is the over-redaction this
 * module's header already refuses ("redacting everything is safe and useless"), so the move is DECLINED until the
 * `address` and `creditCard` shapes are narrowed at their own leaf -- narrowing them is the prerequisite, not the
 * relocation. `redactPII` keeps them where its consumers (voice transcripts, activity payloads) want that recall.
 *
 * THE FUNCTION ITSELF: project one scalar. Numbers and booleans ride verbatim; a string takes both passes above and is
 * then capped; anything JSON cannot serialize (bigint, function, symbol) becomes its type descriptor, because
 * publishing it is not an option the wire has.
 */
function projectScalar(value: unknown): unknown {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number' || typeof value === 'boolean') return value;
	if (typeof value === 'string') {
		return capString(scrubSensitiveSpans(value), TELEMETRY_REDACTION.PUBLISHABLE_VALUE_MAX_CHARS);
	}
	return telemetryRedactionDescriptor(UNSERIALIZABLE_DESCRIPTOR_BY_TYPEOF[typeof value] ?? TELEMETRY_REDACTION_DESCRIPTORS.UNKNOWN);
}

/** Cap a list at MAX_ARRAY_ITEMS, preserving an existing trailing truncation marker so a second pass does not re-truncate. */
function boundedList(items: readonly unknown[], project: (item: unknown) => unknown): unknown[] {
	const carried = items.length > 0 && isDescriptorString(items.at(-1)) ? items.at(-1) : null;
	const body = carried === null ? items : items.slice(0, -1);
	const kept: unknown[] = body.slice(0, TELEMETRY_REDACTION.MAX_ARRAY_ITEMS).map(project);
	const dropped = body.length - kept.length;
	if (carried !== null) kept.push(carried);
	else if (dropped > 0) kept.push(telemetryRedactionDescriptor(TELEMETRY_REDACTION_DESCRIPTORS.ITEMS_TRUNCATED, dropped));
	return kept;
}

/**
 * Split an object's keys four ways. `carried` are marker keys an EARLIER projection pass wrote -- they ride through
 * untouched, which is what keeps a bucketed object idempotent: without it, a second pass would read `<dynamic-keys>` as
 * a non-identifier key name and bucket the bucket.
 */
function partitionKeys(keys: readonly string[]): { named: string[]; carried: string[]; dynamic: number; truncated: number } {
	const carried = keys.filter((key) => isDescriptorString(key));
	const rest = keys.filter((key) => !isDescriptorString(key));
	const publishable = rest.filter(
		(key) => key.length <= TELEMETRY_REDACTION_KEY_NAME_MAX_CHARS && TELEMETRY_REDACTION_KEY_NAME_PATTERN.test(key),
	);
	const named = publishable.slice(0, TELEMETRY_REDACTION_MAX_OBJECT_KEYS);
	return { named, carried, dynamic: rest.length - publishable.length, truncated: publishable.length - named.length };
}

/** Record a bucket count under its descriptor key, summing with any marker carried from an earlier pass. */
function addMarker(out: Record<string, unknown>, kind: string, count: number): void {
	if (count === 0) return;
	const key = telemetryRedactionDescriptor(kind);
	const existing = out[key];
	out[key] = numberOrZero(existing) + count;
}

/**
 * Project one record: keep identifier-shaped key names, withhold a secret-shaped key's value entirely, publish the rest.
 *
 * The key screen SHORT-CIRCUITS before the value is walked, so a credential nested under `authorization: { token: ... }`
 * loses the whole subtree rather than being descended into and published field by field.
 */
function projectObject(record: Record<string, unknown>, depth: number): Record<string, unknown> {
	const { named, carried, dynamic, truncated } = partitionKeys(Object.keys(record));
	const out: Record<string, unknown> = {};
	for (const key of named) {
		out[key] = isSecretKey(key) ? REDACTED_PLACEHOLDER : projectValue(record[key], depth + 1);
	}
	for (const key of carried) out[key] = record[key];
	addMarker(out, TELEMETRY_REDACTION_DESCRIPTORS.KEYS_TRUNCATED, truncated);
	addMarker(out, TELEMETRY_REDACTION_DESCRIPTORS.DYNAMIC_KEYS, dynamic);
	return out;
}

/** The recursive core. Publish-by-default: a value reaches this function whenever no key screen withheld it. */
function projectValue(value: unknown, depth: number): unknown {
	if (depth > TELEMETRY_REDACTION_MAX_DEPTH) return telemetryRedactionDescriptor(TELEMETRY_REDACTION_DESCRIPTORS.DEPTH_CAPPED);
	if (isDescriptorString(value)) return value;
	if (Array.isArray(value)) return boundedList(value, (item) => projectValue(item, depth + 1));
	if (isPlainObject(value)) return projectObject(value, depth);
	return projectScalar(value);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): THE publication projection. Apply to any payload before it can
 * reach a published telemetry column. Total (never throws), idempotent, and structure-preserving.
 */
export function redactTelemetryPayload(value: unknown): unknown {
	if (value === null || value === undefined) return null;
	return projectValue(value, 0);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the ERROR leg's projection.
 *
 * Under the publish-by-default posture this is no longer a DIFFERENT posture from the payload legs -- both scrub
 * credential spans and cap -- but it stays a separate entry for two reasons: its limit derives from a different
 * quantity (one free-text field, not a whole row), and a non-string error from a FOREIGN producer must fall through to
 * the structural projection rather than be coerced into one.
 */
export function redactTelemetryErrorText(value: unknown): unknown {
	if (value === null || value === undefined) return null;
	// A non-string error from a FOREIGN producer (the door re-emits a CLI-posted row verbatim) gets the ordinary
	// structural projection -- span scrubbing is the right treatment only for free text.
	if (typeof value !== 'string') return redactTelemetryPayload(value);
	// Same two span scrubs as a published payload string, in the same order -- an error message routinely quotes the
	// request that produced it, so it is exactly as likely to carry a credential or an address as any other field.
	return capString(scrubSensitiveSpans(value), TELEMETRY_REDACTION.ERROR_MAX_CHARS);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): STRING-TYPED form of {@link redactTelemetryErrorText}, for the legs
 * whose error is already narrowed to `string | null` -- `FlowRunRecord.error`, and every kaizen envelope built from one.
 *
 * IT IS A TYPE ADAPTER, NOT A SECOND POSTURE. The whole body delegates; there is exactly one implementation of the
 * error projection and this returns whatever that one produced. It exists because `redactTelemetryErrorText` must widen
 * its return to `unknown` for the FOREIGN-producer arm (a non-string error falls through to the structural projection),
 * and a `string | null` caller would otherwise have to re-spell the `typeof === 'string'` narrowing at every site --
 * which is three copies of one decision, in three files, on the exact field this projection exists to guard.
 *
 * NULL IS PRESERVED so `?? fallback` still reads at the call site: a run with no error text must reach its caller's
 * fallback description, never the string `'null'`.
 */
export function redactTelemetryErrorString(value: string | null): string | null {
	const projected = redactTelemetryErrorText(value);
	return stringOrNull(projected);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link redactTelemetryErrorString} for a journal member the record
 * types as TEXT (a target, a reason, a detail) -- the screened text, and the empty string where there was none, so every
 * journal composer writes the member without re-spelling the `?? ''`.
 */
export function redactedJournalText(value: string | null): string {
	return redactTelemetryErrorString(value) ?? '';
}

/** The three published payload legs a node-result row may carry, each projected before the row leaves the process. */
type NodeResultPayloadLegs = { input?: unknown; output?: unknown; error?: unknown };

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): project the published payload legs of one node-result row.
 * Structurally typed rather than importing `FlowStepResultRecord`, so the const leaf stays value-import-free of types
 * and the same helper serves the typed transports and any structurally-shaped rows a transport hands it.
 *
 * THREE legs, not two. `error` is published as the step row's `error` leg on the retained run journal, so a spread
 * carrying it through untouched would put a credential-bearing throw message into the same row whose `input`/`output`
 * had just been projected. (The analytics Steps tab reads the span leg's `span_status_message`, which carries the SAME
 * text through its own `scrubCredentialSpans` read-site scrub, so the projection here is not the only defense but it
 * is the one this row owes.)
 */
function redactNodeResultRow<T extends NodeResultPayloadLegs>(row: T): T {
	return {
		...row,
		input: redactTelemetryPayload(row.input),
		output: redactTelemetryPayload(row.output),
		error: redactTelemetryErrorText(row.error),
	};
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): row-set form of {@link redactNodeResultRow}. */
export function redactStepResultRows<T extends NodeResultPayloadLegs>(rows: readonly T[]): T[] {
	return rows.map(redactNodeResultRow);
}
